import React, { useState } from 'react';
import {
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  FileText,
  Package,
  Building,
  Calendar,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Truck,
  Download,
  Send,
  MoreHorizontal,
  Activity
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplier: string;
  supplierContact: string;
  status: 'draft' | 'sent' | 'acknowledged' | 'partially_received' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  orderDate: string;
  expectedDelivery: string;
  totalAmount: number;
  totalItems: number;
  receivedItems: number;
  createdBy: string;
  approvedBy?: string;
  items: PurchaseOrderItem[];
}

interface PurchaseOrderItem {
  id: string;
  medication: string;
  genericName: string;
  strength: string;
  packSize: string;
  quantityOrdered: number;
  quantityReceived: number;
  unitCost: number;
  totalCost: number;
  leadTime: number;
  status: 'pending' | 'received' | 'partial' | 'backordered';
}

const PurchaseOrders: React.FC = () => {
  const { user } = useAuth();
  const [selectedPO, setSelectedPO] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSupplier, setFilterSupplier] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const mockPurchaseOrders: PurchaseOrder[] = [
    {
      id: 'po-001',
      poNumber: 'PO-2024-001',
      supplier: 'Sigma Healthcare',
      supplierContact: 'orders@sigma.com.au',
      status: 'sent',
      priority: 'high',
      orderDate: format(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      expectedDelivery: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
      totalAmount: 15750.50,
      totalItems: 12,
      receivedItems: 0,
      createdBy: 'Sarah Johnson',
      approvedBy: 'Michael Chen',
      items: [
        {
          id: 'item-001',
          medication: 'Atorvastatin 20mg',
          genericName: 'Atorvastatin',
          strength: '20mg',
          packSize: '30 tablets',
          quantityOrdered: 50,
          quantityReceived: 0,
          unitCost: 25.50,
          totalCost: 1275.00,
          leadTime: 3,
          status: 'pending'
        },
        {
          id: 'item-002',
          medication: 'Metformin 500mg',
          genericName: 'Metformin',
          strength: '500mg',
          packSize: '100 tablets',
          quantityOrdered: 30,
          quantityReceived: 0,
          unitCost: 18.75,
          totalCost: 562.50,
          leadTime: 2,
          status: 'pending'
        }
      ]
    },
    {
      id: 'po-002',
      poNumber: 'PO-2024-002',
      supplier: 'API (Australian Pharmaceutical Industries)',
      supplierContact: 'orders@api.net.au',
      status: 'partially_received',
      priority: 'medium',
      orderDate: format(new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      expectedDelivery: format(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      totalAmount: 8950.25,
      totalItems: 8,
      receivedItems: 5,
      createdBy: 'John Smith',
      approvedBy: 'Sarah Johnson',
      items: []
    },
    {
      id: 'po-003',
      poNumber: 'PO-2024-003',
      supplier: 'Symbion',
      supplierContact: 'orders@symbion.com.au',
      status: 'draft',
      priority: 'low',
      orderDate: format(new Date(), 'yyyy-MM-dd'),
      expectedDelivery: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
      totalAmount: 12300.00,
      totalItems: 15,
      receivedItems: 0,
      createdBy: 'Michael Chen',
      items: []
    }
  ];

  const suppliers = ['Sigma Healthcare', 'API (Australian Pharmaceutical Industries)', 'Symbion', 'Pfizer', 'Bayer'];

  const filteredPOs = mockPurchaseOrders.filter(po => {
    const matchesSearch = po.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         po.supplier.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || po.status === filterStatus;
    const matchesSupplier = filterSupplier === 'all' || po.supplier === filterSupplier;
    return matchesSearch && matchesStatus && matchesSupplier;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'sent': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'acknowledged': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'partially_received': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <Edit className="h-4 w-4" />;
      case 'sent': return <Send className="h-4 w-4" />;
      case 'acknowledged': return <CheckCircle className="h-4 w-4" />;
      case 'partially_received': return <Package className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const handleCreatePO = () => {
    setShowCreateModal(true);
  };

  const handleSendPO = (poId: string) => {
    console.log(`Sending PO ${poId}`);
  };

  const handleReceiveItems = (poId: string) => {
    console.log(`Receiving items for PO ${poId}`);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Purchase Orders 📋
          </h1>
          <p className="text-gray-600 mt-2">Create, manage, and track purchase orders with suppliers for {user?.branch}.</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleCreatePO}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Create PO</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export Data</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
            <FileText className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Active POs</p>
            <p className="text-2xl font-bold text-gray-900">{mockPurchaseOrders.filter(po => po.status !== 'completed' && po.status !== 'cancelled').length}</p>
          </div>
        </div>
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-orange-100 text-orange-600">
            <Clock className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Pending Delivery</p>
            <p className="text-2xl font-bold text-gray-900">{mockPurchaseOrders.filter(po => po.status === 'sent' || po.status === 'acknowledged').length}</p>
          </div>
        </div>
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-green-100 text-green-600">
            <DollarSign className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Value</p>
            <p className="text-2xl font-bold text-gray-900">${mockPurchaseOrders.reduce((sum, po) => sum + po.totalAmount, 0).toLocaleString()}</p>
          </div>
        </div>
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-purple-100 text-purple-600">
            <Package className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Items Pending</p>
            <p className="text-2xl font-bold text-gray-900">{mockPurchaseOrders.reduce((sum, po) => sum + (po.totalItems - po.receivedItems), 0)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search PO number, supplier..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="partially_received">Partially Received</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Suppliers</option>
            {suppliers.map(supplier => (
              <option key={supplier} value={supplier}>{supplier}</option>
            ))}
          </select>

          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Apply Filters</span>
          </button>
        </div>
      </div>

      {/* Purchase Orders List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* PO List */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Purchase Orders</h3>
          <div className="space-y-4">
            {filteredPOs.map((po) => (
              <div 
                key={po.id} 
                className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                  selectedPO === po.id ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedPO(po.id)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <Building className="h-4 w-4 text-gray-600" />
                      <span className="font-semibold text-gray-900">{po.poNumber}</span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(po.priority)}`}>
                      {po.priority.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(po.status)} flex items-center space-x-1`}>
                      {getStatusIcon(po.status)}
                      <span>{po.status.replace('_', ' ')}</span>
                    </span>
                  </div>
                </div>
                
                <div className="mb-3">
                  <p className="font-medium text-gray-900">{po.supplier}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                    <span className="flex items-center space-x-1">
                      <Package className="h-4 w-4" />
                      <span>{po.receivedItems}/{po.totalItems} items</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <DollarSign className="h-4 w-4" />
                      <span>${po.totalAmount.toLocaleString()}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{format(new Date(po.expectedDelivery), 'dd/MM')}</span>
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Created: {format(new Date(po.orderDate), 'dd/MM/yyyy')} by {po.createdBy}
                  </div>
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${(po.receivedItems / po.totalItems) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PO Details */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          {selectedPO ? (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Purchase Order Details</h3>
                <div className="flex space-x-2">
                  {(() => {
                    const po = mockPurchaseOrders.find(p => p.id === selectedPO);
                    if (!po) return null;
                    
                    return (
                      <>
                        {po.status === 'draft' && (
                          <button 
                            onClick={() => handleSendPO(po.id)}
                            className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center space-x-1"
                          >
                            <Send className="h-4 w-4" />
                            <span>Send PO</span>
                          </button>
                        )}
                        {(po.status === 'sent' || po.status === 'acknowledged' || po.status === 'partially_received') && (
                          <button 
                            onClick={() => handleReceiveItems(po.id)}
                            className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm flex items-center space-x-1"
                          >
                            <Package className="h-4 w-4" />
                            <span>Receive</span>
                          </button>
                        )}
                        <button className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 text-sm">
                          <Eye className="h-4 w-4" />
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>

              {(() => {
                const po = mockPurchaseOrders.find(p => p.id === selectedPO);
                if (!po) return null;

                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600">Supplier</p>
                        <p className="font-medium text-gray-900">{po.supplier}</p>
                        <p className="text-sm text-gray-500">{po.supplierContact}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Expected Delivery</p>
                        <p className="font-medium text-gray-900">{format(new Date(po.expectedDelivery), 'dd/MM/yyyy')}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Amount</p>
                        <p className="font-medium text-gray-900">${po.totalAmount.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Progress</p>
                        <p className="font-medium text-gray-900">{po.receivedItems}/{po.totalItems} items received</p>
                      </div>
                    </div>

                    {po.approvedBy && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-green-800 font-medium">Approved by: {po.approvedBy}</span>
                        </div>
                      </div>
                    )}

                    {po.items.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-semibold text-gray-900">Order Items</h4>
                        {po.items.map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{item.medication}</p>
                              <p className="text-sm text-gray-600">{item.genericName} • {item.strength} • {item.packSize}</p>
                              <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                                <span>Qty: {item.quantityOrdered}</span>
                                <span>Unit: ${item.unitCost}</span>
                                <span>Lead: {item.leadTime} days</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">${item.totalCost.toLocaleString()}</p>
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                item.status === 'received' ? 'bg-green-100 text-green-800' :
                                item.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                item.status === 'backordered' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {item.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>Select a purchase order to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrders;
